/**
 * Datoforespørselen i nettleseren — arrangørens side (`/date-requests`) og husets (`/venue-enquiries`).
 *
 * HVER SKRIVING FEIER BEGGE PREFIKSENE, pluss konsertene og Spillesteder-siden: et svar endrer
 * husets rad, arrangørens kopi, konserten (status, dato, hold) og kalenderen på salens side.
 * Et hus som både arrangerer og har en scene har alle fire på skjermen samtidig.
 */
import {
  DATE_REQUEST_REFUSALS,
  dateRequestArtistOptionsSchema,
  dateRequestDtoSchema,
  dateRequestListResponseSchema,
  venueEnquiryDetailSchema,
  venueEnquiryListResponseSchema,
  venueEnquiryMessageDtoSchema,
  venueEnquiryMessageListSchema,
  venueEnquirySummarySchema,
  type AnswerVenueEnquiry,
  type DateRequestArtistOptions,
  type DateRequestDto,
  type DateRequestListResponse,
  type ExtendVenueHold,
  type SendDateRequest,
  type VenueEnquiryDetail,
  type VenueEnquiryListResponse,
  type VenueEnquiryMessageDto,
  type VenueEnquiryMessageList,
  type VenueEnquirySummary,
  type VenueEnquiryTab,
} from '@tvitvi/shared';
import {
  infiniteQueryOptions,
  queryOptions,
  useMutation,
  type UseMutationResult,
} from '@tanstack/react-query';

import { CACHE } from '@/api/cache-tiers';
import { apiRequest } from '@/api/client';
import { ApiError } from '@/api/errors';
import { qk } from '@/api/query-keys';

const REQUESTS = '/date-requests';
const ENQUIRIES = '/venue-enquiries';

const invalidates = (orgId: string) => ({
  invalidates: [
    qk.dateRequests(orgId),
    qk.venueEnquiries(orgId),
    qk.orgConcerts(orgId),
    qk.venueMarket(orgId),
    /* Leien er et avtaleark (274.8 b): husets svar med leie er en ny versjon, trekk trekker arket. */
    qk.dealSheets(orgId),
    /*
     * QA16 funn 28 og 298.5: et svar endrer husets kalender, Oversikt og arrangementene (en bekreftelse
     * lager et). Uten dette sto merket og kalenderen med det gamle til neste omlasting.
     */
    qk.venueHouse(orgId),
  ],
});

const REFUSALS: ReadonlySet<string> = new Set(DATE_REQUEST_REFUSALS);

/**
 * Grunnen basen avviste et svar med (`DATE_REQUEST_REFUSALS`), eller null. Koden avgjør, ikke
 * stien: «Forleng hold» svarer på `holdUntil`, en passert dato på `requestedOn` (222.42).
 */
export function refusalOf(error: unknown): string | null {
  if (!(error instanceof ApiError)) return null;
  return error.details.find((detail) => REFUSALS.has(detail.code))?.code ?? null;
}

// ------------------------------------------------------------------------- arrangøren

export function dateRequestsQueryOptions(orgId: string, concertId: string) {
  return queryOptions({
    queryKey: qk.dateRequestList(orgId, concertId),
    queryFn: ({ signal }): Promise<DateRequestListResponse> =>
      apiRequest({
        path: REQUESTS,
        query: { concertId },
        schema: dateRequestListResponseSchema,
        signal,
      }),
    ...CACHE.list,
  });
}

export function artistOptionsQueryOptions(orgId: string) {
  return queryOptions({
    queryKey: qk.dateRequestArtistOptions(orgId),
    queryFn: ({ signal }): Promise<DateRequestArtistOptions> =>
      apiRequest({
        path: `${REQUESTS}/artist-options`,
        schema: dateRequestArtistOptionsSchema,
        signal,
      }),
    ...CACHE.list,
  });
}

export function dateRequestMessagesQueryOptions(orgId: string, requestId: string) {
  return queryOptions({
    queryKey: qk.dateRequestMessages(orgId, requestId),
    queryFn: ({ signal }): Promise<VenueEnquiryMessageList> =>
      apiRequest({
        path: `${REQUESTS}/${requestId}/messages`,
        schema: venueEnquiryMessageListSchema,
        signal,
      }),
    ...CACHE.list,
  });
}

export function useSendDateRequest(
  orgId: string,
): UseMutationResult<DateRequestDto, unknown, SendDateRequest> {
  return useMutation({
    mutationFn: (body: SendDateRequest) =>
      apiRequest({ path: REQUESTS, method: 'POST', body, schema: dateRequestDtoSchema }),
    meta: { ...invalidates(orgId), errorHandledLocally: true },
  });
}

export function useWithdrawDateRequest(
  orgId: string,
): UseMutationResult<DateRequestDto, unknown, string> {
  return useMutation({
    mutationFn: (requestId: string) =>
      apiRequest({
        path: `${REQUESTS}/${requestId}/withdraw`,
        method: 'POST',
        schema: dateRequestDtoSchema,
      }),
    meta: invalidates(orgId),
  });
}

/** «Trekk de andre tilbake» (222.31): fra den bekreftede forespørselen. */
export function useWithdrawOtherDateRequests(
  orgId: string,
): UseMutationResult<DateRequestListResponse, unknown, string> {
  return useMutation({
    mutationFn: (requestId: string) =>
      apiRequest({
        path: `${REQUESTS}/${requestId}/withdraw-others`,
        method: 'POST',
        schema: dateRequestListResponseSchema,
      }),
    meta: { ...invalidates(orgId), errorHandledLocally: true },
  });
}

export function usePostDateRequestMessage(
  orgId: string,
): UseMutationResult<VenueEnquiryMessageDto, unknown, { requestId: string; body: string }> {
  return useMutation({
    mutationFn: ({ requestId, body }: { requestId: string; body: string }) =>
      apiRequest({
        path: `${REQUESTS}/${requestId}/messages`,
        method: 'POST',
        body: { body },
        schema: venueEnquiryMessageDtoSchema,
      }),
    meta: invalidates(orgId),
  });
}

// ------------------------------------------------------------------------------- huset

export interface VenueEnquiryFilters {
  tab: VenueEnquiryTab;
  search?: string | undefined;
  /** QA15-S B3: forespørslene under ett spillesteds saler, eller én sal. */
  festivalId?: string | undefined;
  roomId?: string | undefined;
}

/** Bare satte filtre går i spørringen; tomme utelates, så nøkkelen er den samme for samme utvalg. */
function roomScope(filters: { festivalId?: string | undefined; roomId?: string | undefined }) {
  return {
    ...(filters.festivalId === undefined ? {} : { festivalId: filters.festivalId }),
    ...(filters.roomId === undefined ? {} : { roomId: filters.roomId }),
  };
}

export function venueEnquiriesQueryOptions(orgId: string, filters: VenueEnquiryFilters) {
  return infiniteQueryOptions({
    queryKey: qk.venueEnquiryList(orgId, { ...filters }),
    queryFn: ({ pageParam, signal }): Promise<VenueEnquiryListResponse> =>
      apiRequest({
        path: ENQUIRIES,
        query: {
          tab: filters.tab,
          ...roomScope(filters),
          ...(filters.search === undefined || filters.search === ''
            ? {}
            : { search: filters.search }),
          ...(pageParam === null ? {} : { cursor: pageParam }),
          limit: 50,
        },
        schema: venueEnquiryListResponseSchema,
        signal,
      }),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => (last.hasMore ? last.nextCursor : null),
    ...CACHE.list,
  });
}

export function venueEnquirySummaryQueryOptions(
  orgId: string,
  scope: { festivalId?: string | undefined; roomId?: string | undefined } = {},
) {
  const filters = roomScope(scope);
  return queryOptions({
    queryKey:
      Object.keys(filters).length === 0
        ? qk.venueEnquirySummary(orgId)
        : [...qk.venueEnquirySummary(orgId), filters],
    queryFn: ({ signal }): Promise<VenueEnquirySummary> =>
      apiRequest({
        path: `${ENQUIRIES}/summary`,
        query: filters,
        schema: venueEnquirySummarySchema,
        signal,
      }),
    ...CACHE.list,
  });
}

export function venueEnquiryQueryOptions(orgId: string, enquiryId: string) {
  return queryOptions({
    queryKey: qk.venueEnquiry(orgId, enquiryId),
    queryFn: ({ signal }): Promise<VenueEnquiryDetail> =>
      apiRequest({ path: `${ENQUIRIES}/${enquiryId}`, schema: venueEnquiryDetailSchema, signal }),
    ...CACHE.list,
  });
}

export function venueEnquiryMessagesQueryOptions(orgId: string, enquiryId: string) {
  return queryOptions({
    queryKey: qk.venueEnquiryMessages(orgId, enquiryId),
    queryFn: ({ signal }): Promise<VenueEnquiryMessageList> =>
      apiRequest({
        path: `${ENQUIRIES}/${enquiryId}/messages`,
        schema: venueEnquiryMessageListSchema,
        signal,
      }),
    ...CACHE.list,
  });
}

export function useAnswerVenueEnquiry(
  orgId: string,
): UseMutationResult<VenueEnquiryDetail, unknown, { enquiryId: string; body: AnswerVenueEnquiry }> {
  return useMutation({
    mutationFn: ({ enquiryId, body }: { enquiryId: string; body: AnswerVenueEnquiry }) =>
      apiRequest({
        path: `${ENQUIRIES}/${enquiryId}/answer`,
        method: 'POST',
        body,
        schema: venueEnquiryDetailSchema,
      }),
    meta: { ...invalidates(orgId), errorHandledLocally: true },
  });
}

export function useReleaseVenueHold(
  orgId: string,
): UseMutationResult<VenueEnquiryDetail, unknown, string> {
  return useMutation({
    mutationFn: (enquiryId: string) =>
      apiRequest({
        path: `${ENQUIRIES}/${enquiryId}/release`,
        method: 'POST',
        schema: venueEnquiryDetailSchema,
      }),
    meta: invalidates(orgId),
  });
}

/** «Forleng hold» (224.5). */
export function useExtendVenueHold(
  orgId: string,
): UseMutationResult<VenueEnquiryDetail, unknown, { enquiryId: string; body: ExtendVenueHold }> {
  return useMutation({
    mutationFn: ({ enquiryId, body }: { enquiryId: string; body: ExtendVenueHold }) =>
      apiRequest({
        path: `${ENQUIRIES}/${enquiryId}/extend-hold`,
        method: 'POST',
        body,
        schema: venueEnquiryDetailSchema,
      }),
    meta: { ...invalidates(orgId), errorHandledLocally: true },
  });
}

export function usePostVenueEnquiryMessage(
  orgId: string,
): UseMutationResult<VenueEnquiryMessageDto, unknown, { enquiryId: string; body: string }> {
  return useMutation({
    mutationFn: ({ enquiryId, body }: { enquiryId: string; body: string }) =>
      apiRequest({
        path: `${ENQUIRIES}/${enquiryId}/messages`,
        method: 'POST',
        body: { body },
        schema: venueEnquiryMessageDtoSchema,
      }),
    meta: invalidates(orgId),
  });
}
